import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../types/RootState';
import { initialState } from './index';
import { PlayerStats } from './types';

const selectDomain = (state: RootState) => state.league || initialState;

export const selectMatches = createSelector(
  [selectDomain],
  (leagueState) => leagueState.league?.matches,
);

export const selectMatch = createSelector(
  [selectDomain],
  (leagueState) => leagueState.match,
);

export const selectIsMatchFinished = createSelector([selectMatch], (match) => {
  if (!match?.races) return false;
  return match.races.every((race) =>
    race.runs?.length
      ? race.runs.every((run) => run.status !== 'scheduled')
      : false,
  );
});

export const selectRaces = createSelector(
  [selectDomain],
  (leagueState) => leagueState.match?.races,
);

export const selectCurrentRace = createSelector([selectMatch], (match) => {
  if (!match?.races) return undefined;
  return match.races.find((race) =>
    race.runs?.some((run) => run.status === 'scheduled'),
  );
});

export const selectNextRace = createSelector([selectMatch], (match) => {
  if (!match?.races) return undefined;
  const currentIndex = match.races.findIndex((race) =>
    race.runs?.some((run) => run.status === 'scheduled'),
  );
  if (currentIndex === -1) return undefined;
  return match.races
    .slice(currentIndex + 1)
    .find((race) => race.runs?.some((run) => run.status === 'scheduled'));
});

export const selectCurrentRaceIndex = createSelector([selectMatch], (match) => {
  if (!match?.races) return -1;
  return match.races.findIndex((race) =>
    race.runs?.some((run) => run.status === 'scheduled'),
  );
});

export const selectPastRaces = createSelector([selectRaces], (races) =>
  races
    ?.filter(
      (race) =>
        race.runs?.length &&
        race.runs.every((run) =>
          ['completed', 'disqualified', 'incomplete'].includes(run.status),
        ),
    )
    .reverse(),
);

export const selectPreviousRace = createSelector(
  [selectPastRaces],
  (races) => races?.[0],
);

export const selectUpcomingRaces = createSelector(
  [selectRaces, selectCurrentRace],
  (races, currentRace) => {
    const upcoming =
      races?.filter(
        (race) =>
          race.runs?.length &&
          race.runs.every((run) => run.status === 'scheduled'),
      ) || [];

    /* do not return the same race as current and upcoming */
    return currentRace?.id === upcoming[0]?.id ? upcoming.slice(1) : upcoming;
  },
);

export const selectIsStreaming = createSelector(
  [selectDomain],
  (leagueState) => leagueState.isStreaming,
);

export const selectBestPlayersByTeam = createSelector(
  [selectMatch],
  (match) => {
    if (!match?.teams || !match.races) return {};

    const teams = match.teams;
    const completedRaces = match.races.filter(
      (race) =>
        race.runs &&
        race.runs.length > 0 &&
        race.runs.every((r) => r.status !== 'scheduled'),
    );
    const result: Record<string, PlayerStats[]> = {};

    teams.forEach((team) => {
      const stats = team.players.map((p) => {
        const { nickname, name, avatar, extra } = p;
        const points = extra?.sumPoints ?? 0;
        const playerRuns = completedRaces.flatMap(
          (race) =>
            race.runs?.filter(
              (r) =>
                r.player?.nickname === nickname &&
                r.player?.team?.id === team.id,
            ) ?? [],
        );
        const runs = playerRuns.length;
        const wins = completedRaces.reduce((acc, race) => {
          const bestRun = race.runs?.find((r) => r.player?.extra?.isBest);
          if (
            bestRun &&
            bestRun.player.nickname === nickname &&
            bestRun.player.team.id === team.id
          ) {
            return acc + 1;
          }
          return acc;
        }, 0);

        const winRatio = runs > 0 ? wins / runs : 0;
        return {
          nickname,
          name,
          avatar,
          extra,
          points,
          wins,
          runs,
          winRatio,
        };
      });

      const eligible = stats
        .filter((s) => s.winRatio > 0)
        .sort((a, b) => {
          if (b.winRatio !== a.winRatio) return b.winRatio - a.winRatio;
          return b.points - a.points;
        });
      if (eligible.length === 0) {
        result[team.id] = [];
        return;
      }

      const groups = new Map<number, PlayerStats[]>();
      eligible.forEach((p) => {
        if (!groups.has(p.winRatio)) {
          groups.set(p.winRatio, []);
        }
        groups.get(p.winRatio)!.push(p);
      });

      const winRatiosDesc = Array.from(groups.keys()).sort((a, b) => b - a);
      const topGroup = groups.get(winRatiosDesc[0])!;
      if (topGroup.length > 3) {
        result[team.id] = topGroup;
        return;
      }

      const selected: PlayerStats[] = [];
      for (const ratio of winRatiosDesc) {
        const group = groups.get(ratio)!;
        selected.push(...group);
        if (selected.length >= 3) {
          break;
        }
      }
      result[team.id] = selected;
    });

    return result;
  },
);
